import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, ArrowLeft, Plus, Check, X, Edit2, Trash2 } from 'lucide-react';
import { categoriesService } from '../../services/categoriesService';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';

export const CategoryFormPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    name: '',
    mssqlCode: '',
    requiresInstallationType: false,
  });

  const { data: category } = useQuery({
    queryKey: ['categories', id],
    queryFn: () => categoriesService.getCategory(id!),
    enabled: isEdit,
  });

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name,
        mssqlCode: category.mssqlCode || '',
        requiresInstallationType: category.requiresInstallationType || false,
      });
    }
  }, [category]);

  const saveMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const payload: any = {
        name: data.name,
        mssqlCode: data.mssqlCode || undefined,
        requiresInstallationType: data.requiresInstallationType,
      };

      if (isEdit && id) {
        return categoriesService.updateCategory(id, payload);
      } else {
        return categoriesService.createCategory(payload);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      navigate('/categories');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Chyba při ukládání kategorie';
      alert(errorMessage);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveMutation.mutate(formData);
  };

  // --- Subcategory management (only when editing an existing category) ---
  const [newSubcategoryName, setNewSubcategoryName] = useState('');
  const [editingSubcategoryId, setEditingSubcategoryId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  const { data: subcategories = [], isSuccess: podkategorieNacteny } = useQuery({
    queryKey: ['subcategories', id],
    queryFn: () => categoriesService.getSubcategories(id!),
    enabled: isEdit,
  });

  // Odkaz „počet podkategorií" v seznamu míří na /categories/:id/edit#podkategorie.
  // React Router skok na kotvu sám neřeší, takže se sem odroluje ručně - až po
  // načtení podkategorií, jinak by blok ještě neměl výšku a skok by minul.
  useEffect(() => {
    if (location.hash !== '#podkategorie' || !podkategorieNacteny) return;
    document.getElementById('podkategorie')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash, podkategorieNacteny]);

  const createSubcategoryMutation = useMutation({
    mutationFn: (name: string) => categoriesService.createSubcategory(id!, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subcategories', id] });
      queryClient.invalidateQueries({ queryKey: ['categories', id] });
      setNewSubcategoryName('');
    },
    onError: (error: any) => {
      alert(error.response?.data?.message || 'Chyba při vytváření podkategorie');
    },
  });

  const updateSubcategoryMutation = useMutation({
    mutationFn: ({ subcategoryId, name }: { subcategoryId: string; name: string }) =>
      categoriesService.updateSubcategory(subcategoryId, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subcategories', id] });
      setEditingSubcategoryId(null);
      setEditingName('');
    },
    onError: (error: any) => {
      alert(error.response?.data?.message || 'Chyba při úpravě podkategorie');
    },
  });

  const deleteSubcategoryMutation = useMutation({
    mutationFn: (subcategoryId: string) => categoriesService.deleteSubcategory(subcategoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subcategories', id] });
      queryClient.invalidateQueries({ queryKey: ['categories', id] });
    },
    onError: (error: any) => {
      alert(error.response?.data?.message || 'Chyba při mazání podkategorie');
    },
  });

  const handleAddSubcategory = () => {
    const name = newSubcategoryName.trim();
    if (name) createSubcategoryMutation.mutate(name);
  };

  const handleUpdateSubcategory = () => {
    const name = editingName.trim();
    if (editingSubcategoryId && name) {
      updateSubcategoryMutation.mutate({ subcategoryId: editingSubcategoryId, name });
    }
  };

  const cancelEdit = () => {
    setEditingSubcategoryId(null);
    setEditingName('');
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <Button variant="outline" onClick={() => navigate('/categories')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zpět na kategorie
        </Button>
        <h1 className="text-3xl font-bold text-gray-900">
          {isEdit ? 'Upravit kategorii' : 'Nová kategorie'}
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="space-y-6">
            <Input
              label="Název kategorie *"
              maxLength={150}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="Např. Elektronika, Obuv, atd."
            />

            <div>
              <Input
                label="SKP kód"
                value={formData.mssqlCode}
                onChange={(e) => setFormData({ ...formData, mssqlCode: e.target.value })}
                placeholder="Kód sortimentu pro párování s ERP"
              />
              <p className="mt-1 text-sm text-gray-500">Volitelné pole pro mapování s externím systémem</p>
            </div>

            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.requiresInstallationType}
                  onChange={(e) => setFormData({ ...formData, requiresInstallationType: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700">Rozlišovat vestavné / volně stojící</span>
              </label>
              <p className="mt-1 text-sm text-gray-500">
                Když je zapnuto, u produktů v této kategorii je nutné zvolit typ spotřebiče — Vestavné, nebo Volně stojící.
              </p>
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/categories')}
            >
              Zrušit
            </Button>
            <Button
              type="submit"
              disabled={saveMutation.isPending}
            >
              <Save className="w-4 h-4 mr-2" />
              {saveMutation.isPending ? 'Ukládám...' : 'Uložit'}
            </Button>
          </div>
        </div>
      </form>

      {isEdit && (
        <div id="podkategorie" className="bg-white rounded-lg shadow p-6 mt-6 scroll-mt-4">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Podkategorie</h2>

          {/* Add new subcategory */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Přidat novou podkategorii</label>
            <div className="flex gap-2">
              <input
                type="text"
                maxLength={150}
                value={newSubcategoryName}
                onChange={(e) => setNewSubcategoryName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddSubcategory();
                  }
                }}
                placeholder="Název podkategorie"
                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm"
              />
              <Button
                type="button"
                onClick={handleAddSubcategory}
                disabled={!newSubcategoryName.trim() || createSubcategoryMutation.isPending}
              >
                <Plus className="w-4 h-4 mr-2" />
                Přidat
              </Button>
            </div>
          </div>

          {/* Existing subcategories */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              Existující podkategorie ({subcategories.length})
            </h3>
            {subcategories.length === 0 ? (
              <p className="text-sm text-gray-500 italic">Žádné podkategorie</p>
            ) : (
              <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md">
                {subcategories.map((sub) => (
                  <li key={sub.id} className="p-3 hover:bg-gray-50">
                    {editingSubcategoryId === sub.id ? (
                      <div className="flex gap-2 items-center">
                        <input
                          type="text"
                          maxLength={150}
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleUpdateSubcategory();
                            } else if (e.key === 'Escape') {
                              cancelEdit();
                            }
                          }}
                          className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm"
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={handleUpdateSubcategory}
                          disabled={!editingName.trim() || updateSubcategoryMutation.isPending}
                          className="p-2 text-green-600 hover:text-green-700 disabled:opacity-50"
                          title="Uložit"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={cancelEdit}
                          className="p-2 text-gray-600 hover:text-gray-700"
                          title="Zrušit"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-900 min-w-0 truncate mr-2" title={sub.name}>{sub.name}</span>
                        <div className="flex gap-2 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingSubcategoryId(sub.id);
                              setEditingName(sub.name);
                            }}
                            className="p-2 text-blue-600 hover:text-blue-700"
                            title="Upravit podkategorii"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm(`Opravdu chcete smazat podkategorii "${sub.name}"?`)) {
                                deleteSubcategoryMutation.mutate(sub.id);
                              }
                            }}
                            disabled={deleteSubcategoryMutation.isPending}
                            className="p-2 text-red-600 hover:text-red-700 disabled:opacity-50"
                            title="Smazat podkategorii"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
